"use client";

import React, { useState, useEffect } from "react";
import VideoPlayer from "../components/creative/VideoPlayer";

const XmlTesterPage = () => {
    const [vastXml, setVastXml] = useState("");
    const [videoSrc, setVideoSrc] = useState(null);
    const [trackingEvents, setTrackingEvents] = useState([]);
    const [xmlConfig, setXmlConfig] = useState({
        url: "",
        method: "GET",
        headers: {
            "Content-Type": "application/xml",
        },
        body: "",
    });
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("manual");

    // Fetch XML from URL
    const fetchXmlFromUrl = async () => {
        if (!xmlConfig.url.trim()) return;

        setIsLoading(true);
        try {
            const options = {
                method: xmlConfig.method,
                headers: xmlConfig.headers,
            };

            if (xmlConfig.method === "POST" && xmlConfig.body) {
                options.body = xmlConfig.body;
            }

            const response = await fetch(xmlConfig.url, options);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const xmlText = await response.text();
            setVastXml(xmlText);

            setTrackingEvents((prev) => [
                ...prev,
                {
                    name: "XML Load",
                    url: xmlConfig.url,
                    status: "success",
                    timestamp: new Date(),
                    responseTime: 0,
                },
            ]);
        } catch (error) {
            console.error("Failed to fetch XML:", error);

            setTrackingEvents((prev) => [
                ...prev,
                {
                    name: "XML Load",
                    url: xmlConfig.url,
                    status: "error",
                    timestamp: new Date(),
                    responseTime: 0,
                },
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    // Parse XML
    useEffect(() => {
        if (!vastXml) return;

        try {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(vastXml, "text/xml");

            const parseError = xmlDoc.querySelector("parsererror");
            if (parseError) throw new Error("Invalid XML format");

            const mediaFile = xmlDoc.querySelector("MediaFile");
            const src = mediaFile?.textContent || null;
            setVideoSrc(src);

            const events = [];

            xmlDoc.querySelectorAll("Impression").forEach((node) =>
                events.push({
                    name: "Impression",
                    url: node.textContent || "",
                    status: "pending",
                })
            );

            xmlDoc.querySelectorAll("Tracking").forEach((node) =>
                events.push({
                    name: node.getAttribute("event") || "",
                    url: node.textContent || "",
                    status: "pending",
                })
            );

            xmlDoc.querySelectorAll("Error").forEach((node) =>
                events.push({
                    name: "Error",
                    url: node.textContent || "",
                    status: "pending",
                })
            );

            const standardEvents = [
                "start",
                "Impression",
                "firstQuartile",
                "midpoint",
                "thirdQuartile",
                "mute",
                "unmute",
                "pause",
                "resume",
                "complete",
            ];

            standardEvents.forEach((eventName) => {
                if (!events.find((e) => e.name === eventName)) {
                    events.push({ name: eventName, url: "", status: "pending" });
                }
            });

            setTrackingEvents(events);
        } catch (err) {
            console.error("Failed to parse VAST XML", err);
            setVideoSrc(null);
            setTrackingEvents([]);
        }
    }, [vastXml]);

    const updateEventStatus = (name, status) => {
        setTrackingEvents((prev) =>
            prev.map((e) =>
                e.name === name
                    ? {
                        ...e,
                        status: status || "fired",
                        timestamp: new Date(),
                        responseTime: 0,
                    }
                    : e
            )
        );
    };

    const formatTime = (timestamp) => {
        return timestamp.toLocaleTimeString("en-US", {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "pending":
                return "border-l-yellow-500 bg-yellow-50/50";
            case "fired":
            case "success":
                return "border-l-green-500 bg-green-50/50";
            case "error":
                return "border-l-red-500 bg-red-50/50";
            default:
                return "border-l-gray-500 bg-gray-50/50";
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "pending":
                return "⏳";
            case "fired":
                return "🚀";
            case "success":
                return "✅";
            case "error":
                return "❌";
            default:
                return "⏳";
        }
    };

    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'pending': return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
            case 'fired': return 'bg-green-100 text-green-800 border border-green-200';
            case 'success': return 'bg-green-100 text-green-800 border border-green-200';
            case 'error': return 'bg-red-100 text-red-800 border border-red-200';
            default: return 'bg-gray-100 text-gray-800 border border-gray-200';
        }
    };

    return (
        <div className="min-h-screen p-6 pt-0">
            <div className="max-w-8xl mx-auto">
                {/* Header */}
                <div className="text-start mb-4">
                    <div className="inline-flex items-center gap-3 backdrop-blur-sm rounded-2xl px-0 py-1 border border-white/20 mb-1">
                        <h1 className="text-3xl font-bold text-slate-900">
                            VAST XML Tester
                        </h1>
                    </div>
                    <p className="text-sm text-slate-500">
                        Test and monitor your VAST XML tracking events in real-time with our professional testing tool
                    </p>
                </div>

                {/* Configuration Tabs */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden mb-8">
                    <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-3 py-2">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                                </svg>
                            </div>
                            <h2 className="text-md font-semibold text-white">XML Configuration</h2>
                        </div>
                    </div>

                    <div className="p-6">
                        {/* Tab Navigation */}
                        <div className="flex border-b border-slate-200 mb-6">
                            <button
                                onClick={() => setActiveTab('manual')}
                                className={`px-4 py-2 font-medium text-sm border-b-2 cursor-pointer transition-colors ${activeTab === 'manual'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-slate-500 hover:text-slate-700'
                                    }`}
                            >
                                📝 Manual Input
                            </button>
                            <button
                                onClick={() => setActiveTab('config')}
                                className={`px-4 py-2 font-medium text-sm border-b-2 cursor-pointer transition-colors ${activeTab === 'config'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-slate-500 hover:text-slate-700'
                                    }`}
                            >
                                ⚙️ URL Configuration
                            </button>
                        </div>

                        {/* Manual Input Tab */}
                        {activeTab === 'manual' && (
                            <div className="space-y-4">
                                <textarea
                                    value={vastXml}
                                    onChange={(e) => setVastXml(e.target.value)}
                                    className="w-full h-72 p-4 border text-black border-slate-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm bg-slate-50/50 transition-all duration-200"
                                    placeholder={`Paste your VAST XML here...`}
                                />
                                <div className="flex items-center justify-between text-sm text-slate-500">
                                    <div className="flex items-center gap-2">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        Paste your VAST XML above to test tracking events
                                    </div>
                                    <div className="text-xs bg-slate-100 px-2 py-1 rounded">
                                        {vastXml.length} characters
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* URL Configuration Tab */}
                        {activeTab === 'config' && (
                            <div className="space-y-6">
                                {/* URL and Method */}
                                <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                                    <div className="w-full">
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                            XML URL
                                        </label>
                                        <input
                                            type="url"
                                            value={xmlConfig.url}
                                            onChange={(e) => setXmlConfig(prev => ({ ...prev, url: e.target.value }))}
                                            placeholder="https://example.com/vast.xml"
                                            className="w-full p-3 border text-black border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                    <div className="hidden">
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                            Method
                                        </label>
                                        <select
                                            value={xmlConfig.method}
                                            onChange={(e) => setXmlConfig(prev => ({ ...prev, method: e.target.value }))}
                                            className="w-full p-3 border text-black border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="GET">GET</option>
                                            <option value="POST">POST</option>
                                        </select>
                                    </div>
                                </div>


                                {/* POST Body */}
                                {xmlConfig.method === 'POST' && (
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                            Request Body
                                        </label>
                                        <textarea
                                            value={xmlConfig.body}
                                            onChange={(e) => setXmlConfig(prev => ({ ...prev, body: e.target.value }))}
                                            placeholder="Enter request body for POST requests..."
                                            className="w-full h-32 p-3 border text-black border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
                                        />
                                    </div>
                                )}

                                {/* Fetch Button */}
                                <div className="flex items-center gap-4 pt-0">
                                    <button
                                        onClick={fetchXmlFromUrl}
                                        disabled={!xmlConfig.url.trim() || isLoading}
                                        className="px-6 py-3 bg-gradient-to-r from-slate-800 to-slate-700 text-white rounded-lg hover:bg-blue-700 disabled:bg-slate-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                                    >
                                        {isLoading ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                Loading...
                                            </>
                                        ) : (
                                            <>
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Fetch XML
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Main Content Area - Input and Player Side by Side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    {/* XML Preview Card */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden">
                        <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-3 py-2">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <h2 className="text-md font-semibold text-white">XML Preview</h2>
                            </div>
                        </div>
                        <div className="p-6">
                            {vastXml ? (
                                <div className="h-72 overflow-auto bg-slate-50 rounded-lg border border-slate-200 p-4">
                                    <pre className="text-sm text-slate-700 whitespace-pre-wrap font-mono">
                                        {vastXml}
                                    </pre>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center h-72 bg-slate-50 rounded-xl border-2 border-dashed border-slate-300">
                                    <svg className="w-12 h-12 text-slate-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <p className="text-slate-500 text-center mb-2">XML content will appear here</p>
                                    <p className="text-slate-400 text-sm text-center">
                                        {activeTab === 'manual'
                                            ? 'Paste XML manually or switch to URL configuration'
                                            : 'Fetch XML from URL to see the content here'}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Video Player Card */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden">
                        <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-3 py-2">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <h2 className="text-md font-semibold text-white">Video Preview</h2>
                            </div>
                        </div>
                        <div className="p-6">
                            {videoSrc ? (
                                <VideoPlayer
                                    src={videoSrc}
                                    trackingEvents={trackingEvents}
                                    onEventFired={updateEventStatus}
                                />
                            ) : (
                                <div className="flex flex-col items-center justify-center h-72 bg-slate-50 rounded-xl border-2 border-dashed border-slate-300">
                                    <svg className="w-12 h-12 text-slate-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                    <p className="text-slate-500 text-center mb-2">Video URL will appear here</p>
                                    <p className="text-slate-400 text-sm text-center">After loading valid VAST XML with MediaFile</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Tracking Events - Full Width Below */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden">
                    <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-3 py-2">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                </div>
                                <div>
                                    <h2 className="text-md font-semibold text-white">Tracking Events Monitor</h2>
                                    <p className="text-emerald-100 text-sm font-medium">
                                        {trackingEvents.filter(e => e.status === 'error' || e.status === 'fired' || e.status === 'success').length} of {trackingEvents.length} events fired
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="p-6">
                        {trackingEvents.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {trackingEvents.map((event, index) => (
                                    <div
                                        key={index}
                                        className={`border-l-4 rounded-xl p-3 transition-all duration-500 shadow-md hover:shadow-md ${getStatusColor(event.status)} 
                      ${event.status !== 'pending' ? 'animate-pulse-scale' : ''}`}
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white shadow-sm border border-slate-200 flex items-center justify-center text-base font-medium transition-all duration-300">
                                                    {getStatusIcon(event.status)}
                                                </div>
                                                <div className="flex min-w-0 w-full justify-between items-center">
                                                    <div className="font-semibold text-slate-900 capitalize text-sm truncate">
                                                        {event.name.replace(/([A-Z])/g, ' $1').trim()}
                                                    </div>
                                                    <div className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all duration-300 inline-block mt-1 ${getStatusBadgeColor(event.status)}`}>
                                                        {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {event.url && (
                                            <div className="text-xs text-slate-600 font-mono bg-slate-100 px-3 py-2 rounded-lg mb-3 break-all max-h-20 overflow-y-auto">
                                                {event.url}
                                            </div>
                                        )}

                                        {event.timestamp && (
                                            <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-slate-200/50">
                                                <span className="font-medium">{formatTime(event.timestamp)}</span>
                                                {event.responseTime !== undefined && event.responseTime > 0 && (
                                                    <span className="bg-slate-200 px-2 py-1 rounded">
                                                        {event.responseTime}ms
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {!event.timestamp && (
                                            <div className="text-xs text-slate-400 italic pt-2 border-t border-slate-200/50">
                                                Waiting to fire...
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-semibold text-slate-700 mb-2">No Tracking Events Found</h3>
                                <p className="text-slate-500 max-w-md mx-auto">
                                    Load VAST XML manually or via URL to discover and monitor tracking events in real-time.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Add custom animation for status changes */}
            <style jsx global>{`
        @keyframes pulseScale {
          0% { transform: scale(1); }
          50% { transform: scale(1.02); }
          100% { transform: scale(1); }
        }
        .animate-pulse-scale {
          animation: pulseScale 0.5s ease-in-out;
        }
      `}</style>
        </div>
    );
};

export default XmlTesterPage;
